import axios from "axios";
import { toast } from "react-toastify";
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  isAccessTokenExpired,
  getUserIdFromAccessToken,
} from "./authStore";
import { isPublicPath, isBoardPath } from "./routePolicy";
import { isAnonymousPublicBoard } from "./boardAccess";
import { API } from "src/Funcs/APIFuncs/APIFunc";

const ServerToken = import.meta.env.VITE_SERVER_TOKEN;

let refreshInFlight = null;

/**
 * Exchange the refresh token for a fresh access token.
 * Single-flight: every concurrent caller shares the same request, so the
 * axios interceptor and the route gates never fire parallel refreshes.
 * On a definitive server rejection the stored tokens are cleared;
 * transient failures (dropped connection, 5xx) keep them.
 *
 * @returns {Promise<string|null>} the new access token, or null.
 */
export function refreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(null);

  refreshInFlight = axios
    .post(
      `${API}user/refresh`,
      { refreshToken },
      {
        headers: {
          "Content-Type": "application/json",
          "x-auth-token-server": ServerToken || "",
        },
      },
    )
    .then(({ data }) => {
      const newAccessToken = data?.accessToken ?? null;
      const newRefreshToken = data?.refreshToken ?? refreshToken;
      setTokens(newAccessToken, newRefreshToken);
      syncStoredUserId();
      return newAccessToken;
    })
    .catch((err) => {
      // Only a definitive server rejection kills the session. A dropped
      // connection (no response) or a 5xx is transient — keep the tokens
      // so in-flight requests can retry once connectivity returns.
      const status = err?.response?.status;
      if (status && status < 500) clearTokens();
      return null;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

/**
 * The refresh response only carries tokens — recover localStorage("UserID")
 * from the JWT `_id` claim so pages that read it directly keep working even
 * if storage was partially cleared.
 */
function syncStoredUserId() {
  if (typeof localStorage === "undefined") return;
  const userId = getUserIdFromAccessToken();
  if (userId && localStorage.getItem("UserID") !== userId) {
    localStorage.setItem("UserID", userId);
  }
}

/**
 * Ensure the browser holds a usable session before a protected page renders:
 * a fresh access token passes immediately, an expired/missing one triggers a
 * refresh (which is also the server-side validity check for the session).
 *
 * @returns {Promise<boolean>}
 */
export async function ensureSession() {
  if (getAccessToken() && !isAccessTokenExpired()) {
    syncStoredUserId();
    return true;
  }
  if (!getRefreshToken()) return false;
  return Boolean(await refreshAccessToken());
}

/** Login URL carrying a redirect-back param so the user returns after login. */
export function buildLoginUrl(pathname, search = "") {
  const target = `${pathname || "/"}${search || ""}`;
  if (target === "/") return "/";
  return `/?redirect=${encodeURIComponent(target)}`;
}

/** Whether a hard redirect to "/" should happen for the current location. */
function shouldRedirectToLogin() {
  const { pathname } = window.location;
  if (isPublicPath(pathname)) return false;
  if (isBoardPath(pathname) && isAnonymousPublicBoard()) return false;
  return true;
}

/**
 * Called when the session is unrecoverably dead (e.g. refresh token rejected
 * inside the axios interceptor). Clears tokens and hard-redirects to the
 * login page with a redirect-back param — unless the current location is a
 * public path or an anonymous public-board view.
 */
export function handleSessionExpired() {
  if (typeof window === "undefined") return;
  clearTokens();
  if (!shouldRedirectToLogin()) return;
  toast.error("Please Login! Your Token Expired");
  const { pathname, search } = window.location;
  window.location.replace(buildLoginUrl(pathname, search));
}
